import { Fragment, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import Attachments from "../components/Attachments";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const num = (v, d = 0) =>
    Number(v || 0).toLocaleString(undefined, {
        minimumFractionDigits: d,
        maximumFractionDigits: d,
    });

const dt = (v) => {
    if (!v) return "—";
    try {
        return new Date(v).toLocaleString("en-PH", { timeZone: "Asia/Manila" });
    } catch {
        return v;
    }
};

function deliveryTotals(d) {
    const lines = d.lines || [];
    return {
        qty: lines.reduce((a, l) => a + Number(l.deliver_quantity ?? l.quantity ?? 0), 0),
        lines: lines.length,
    };
}

function signedBy(d) {
    return d.signature?.signed_by || "—";
}

function signedAt(d) {
    return d.signature?.signed_at || null;
}

function sapLabel(d) {
    if (d.sap_status === "posted_to_sap") {
        return d.sap_docnum ? `SAP DN ${d.sap_docnum}` : "Posted";
    }
    if (d.sap_status === "sap_posting_failed") return "Failed";
    return d.sap_status || "not_posted";
}

export function Picklists() {
    const [rows, setRows] = useState([]);
    const [busyId, setBusyId] = useState(null);
    const [releasePick, setReleasePick] = useState(null);
    const [releaseLines, setReleaseLines] = useState({});

    const load = async () => {
        const { data } = await api.get("/picklists");
        setRows(data);
    };

    useEffect(() => {
        load();
    }, []);

    const lineMaxQty = (l) =>
        Number(
            l.remaining_quantity ??
            l.pick_quantity ??
            l.quantity ??
            0
        );

    const openReleaseModal = (p) => {
        const initial = {};
        (p.lines || []).forEach((l) => {
            const id = l.line_id || l.id;
            initial[id] = lineMaxQty(l);
        });
        setReleasePick(p);
        setReleaseLines(initial);
    };

    const updateReleaseQty = (line, value) => {
        const id = line.line_id || line.id;
        setReleaseLines((prev) => ({
            ...prev,
            [id]: value,
        }));
    };

    const releaseSelectedQty = async () => {
        if (!releasePick) return;

        const lines = (releasePick.lines || [])
            .map((l) => {
                const id = l.line_id || l.id;
                const releaseQty = Number(releaseLines[id] || 0);
                return {
                    line_id: id,
                    release_quantity: releaseQty,
                };
            })
            .filter((l) => l.release_quantity > 0);

        if (lines.length === 0) {
            toast.error("Enter at least one release quantity.");
            return;
        }

        for (const l of releasePick.lines || []) {
            const id = l.line_id || l.id;
            const qty = Number(releaseLines[id] || 0);
            const max = lineMaxQty(l);

            if (qty > max) {
                toast.error(`${l.item_code} exceeds remaining pick quantity.`);
                return;
            }
        }

        setBusyId(releasePick.id);
        try {
            await api.post(`/picklists/${releasePick.id}/release`, { lines });
            toast.success(`Picklist ${releasePick.doc_no} released`);
            setReleasePick(null);
            setReleaseLines({});
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusyId(null);
        }
    };

    const createDelivery = async (p) => {
        setBusyId(p.id);
        try {
            await api.post("/deliveries", {
                picklist_id: p.id,
                lines: [],
            });
            toast.success(`Delivery scheduled from ${p.doc_no}`);
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusyId(null);
        }
    };

    const totalQty = (p) =>
        (p.lines || []).reduce((a, l) => a + Number(l.released_qty || l.pick_quantity || l.remaining_quantity || l.quantity || 0), 0);

    return (
        <div className="p-6 space-y-4" data-testid="picklists-page">
            <div>
                <div className="sc-section-title">Logistics</div>
                <h1 className="text-2xl font-semibold tracking-tight">Picklists</h1>
                <p className="text-sm text-muted-foreground mt-1">
                    SRS flow: Sales Order → Picklist → Release selected quantity → Delivery.
                </p>
            </div>

            <div className="sc-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full sc-table min-w-[1050px]">
                        <thead>
                            <tr>
                                <th>Doc no</th>
                                <th>SO</th>
                                <th>Warehouse</th>
                                <th>Status</th>
                                <th className="text-right">Lines</th>
                                <th className="text-right">Qty</th>
                                <th>Delivery</th>
                                <th className="text-right">Actions</th>
                            </tr>
                        </thead>

                        <tbody>
                            {rows.length === 0 && (
                                <tr>
                                    <td colSpan={8} className="text-center py-8 text-muted-foreground">
                                        No picklists.
                                    </td>
                                </tr>
                            )}

                            {rows.map((p) => {
                                const canRelease = p.status === "open";
                                const canCreateDelivery = p.status === "released";

                                return (
                                    <tr key={p.id}>
                                        <td className="font-mono">{p.doc_no}</td>
                                        <td className="font-mono text-xs">{p.sales_order_no}</td>
                                        <td className="font-mono">{p.warehouse_code}</td>
                                        <td><StatusBadge status={p.status} /></td>
                                        <td className="sc-num">{p.lines?.length || 0}</td>
                                        <td className="sc-num">{num(totalQty(p))}</td>
                                        <td className="font-mono text-xs">{p.delivery_no || "—"}</td>
                                        <td className="text-right">
                                            <div className="flex justify-end gap-2">
                                                <button
                                                    onClick={() => openReleaseModal(p)}
                                                    disabled={!canRelease || busyId === p.id}
                                                    className="h-8 px-2 rounded border border-border text-xs hover:bg-secondary disabled:opacity-40"
                                                >
                                                    Release
                                                </button>
                                                <button
                                                    onClick={() => createDelivery(p)}
                                                    disabled={!canCreateDelivery || busyId === p.id}
                                                    className="h-8 px-2 rounded border border-border text-xs hover:bg-secondary disabled:opacity-40"
                                                >
                                                    Create Delivery
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {releasePick && (
                <div className="fixed inset-0 bg-black/40 grid place-items-center p-4 z-50" onClick={() => setReleasePick(null)}>
                    <div className="sc-card p-5 w-full max-w-5xl space-y-4 bg-background" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <div className="text-xs uppercase tracking-wider text-muted-foreground">Release Picklist Quantity</div>
                                <div className="text-lg font-semibold">{releasePick.doc_no}</div>
                                <div className="text-sm text-muted-foreground">{releasePick.sales_order_no}</div>
                            </div>
                            <button onClick={() => setReleasePick(null)} className="h-9 px-3 rounded border border-border text-sm">
                                Close
                            </button>
                        </div>

                        <div className="overflow-x-auto border border-border rounded-lg">
                            <table className="w-full sc-table min-w-[850px]">
                                <thead>
                                    <tr>
                                        <th>Item</th>
                                        <th>Description</th>
                                        <th>Warehouse</th>
                                        <th className="text-right">Available to Release</th>
                                        <th className="text-right">Release Qty</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(releasePick.lines || []).map((l) => {
                                        const id = l.line_id || l.id;
                                        const max = lineMaxQty(l);

                                        return (
                                            <tr key={id}>
                                                <td className="font-mono text-xs">{l.item_code}</td>
                                                <td>
                                                    <div>{l.item_name}</div>
                                                    <div className="text-[11px] text-muted-foreground">{l.size || l.description}</div>
                                                </td>
                                                <td className="font-mono">{l.warehouse_code || releasePick.warehouse_code}</td>
                                                <td className="sc-num">{num(max)}</td>
                                                <td className="sc-num">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        max={max}
                                                        step="1"
                                                        className="sc-input h-8 w-32 text-right font-mono"
                                                        value={releaseLines[id] ?? 0}
                                                        onChange={(e) => updateReleaseQty(l, e.target.value)}
                                                    />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        <div className="rounded-md bg-secondary/60 border border-border p-3 text-sm text-muted-foreground">
                            Enter only the quantity to release now. Any remaining Sales Order quantity can be handled by creating another Picklist later.
                        </div>

                        <div className="flex justify-end gap-2">
                            <button onClick={() => setReleasePick(null)} className="h-9 px-3 rounded border border-border text-sm">
                                Cancel
                            </button>
                            <button
                                onClick={releaseSelectedQty}
                                disabled={busyId === releasePick.id}
                                className="h-9 px-4 rounded bg-foreground text-background text-sm disabled:opacity-50"
                            >
                                {busyId === releasePick.id ? "Releasing…" : "Release Selected Qty"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}


export function Deliveries() {
    const [rows, setRows] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [trucks, setTrucks] = useState([]);
    const [trips, setTrips] = useState([]);
    const [editing, setEditing] = useState(null);
    const [viewing, setViewing] = useState(null);
    const [busyId, setBusyId] = useState(null);

    const load = async () => {
        const [d, dr, tr, tp] = await Promise.all([
            api.get("/deliveries"),
            api.get("/drivers"),
            api.get("/trucks"),
            api.get("/trips"),
        ]);

        setRows(d.data);
        setDrivers(dr.data);
        setTrucks(tr.data);
        setTrips(tp.data);

        if (viewing) {
            const fresh = d.data.find((x) => x.id === viewing.id);
            if (fresh) setViewing(fresh);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const summary = useMemo(() => {
        return {
            deliveries: rows.length,
            delivered: rows.filter((d) => d.status === "delivered").length,
            failed: rows.filter((d) => d.sap_status === "sap_posting_failed").length,
            posted: rows.filter((d) => d.sap_status === "posted_to_sap").length,
            qty: rows.reduce((a, d) => a + deliveryTotals(d).qty, 0),
        };
    }, [rows]);

    const assign = async () => {
        try {
            await api.put(`/deliveries/${editing.id}/assign-driver`, {
                driver_code: editing.driver_code,
                truck_code: editing.truck_code,
                trip_code: editing.trip_code,
            });
            toast.success("Assigned");
            setEditing(null);
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const sign = async (d) => {
        const name = prompt("Customer name (signature):");
        if (!name) return;

        try {
            await api.post(`/deliveries/${d.id}/customer-signature`, {
                signature_data_url: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8/x8AAusB9TgwR/0AAAAASUVORK5CYII=",
                signed_by_name: name,
            });
            toast.success("Delivered & signed");
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const postToSap = async (d) => {
        setBusyId(d.id);
        try {
            const { data } = await api.post(`/deliveries/${d.id}/post-to-sap`);
            toast.success(`SAP Delivery posted: ${data.sap_docnum || data.sap_docentry}`);
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
            await load();
        } finally {
            setBusyId(null);
        }
    };

    return (
        <div className="p-3 md:p-6 space-y-5" data-testid="deliveries-page">
            <div>
                <div className="sc-section-title">Logistics</div>
                <h1 className="text-xl md:text-2xl font-semibold tracking-tight">Deliveries</h1>
                <p className="text-sm text-muted-foreground mt-1">
                    Delivery tracking, proof of delivery, SAP posting status, and posting error visibility.
                </p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                <SummaryCard label="Deliveries" value={num(summary.deliveries)} />
                <SummaryCard label="Delivered" value={num(summary.delivered)} />
                <SummaryCard label="SAP Posted" value={num(summary.posted)} />
                <SummaryCard label="SAP Failed" value={num(summary.failed)} />
                <SummaryCard label="Delivered Qty" value={num(summary.qty)} />
            </div>

            <div className="sc-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full sc-table min-w-[1350px]">
                        <thead>
                            <tr>
                                <th>Doc no</th>
                                <th>SO</th>
                                <th>Customer</th>
                                <th>Driver</th>
                                <th>Truck</th>
                                <th>Trip</th>
                                <th>Status</th>
                                <th>SAP Status</th>
                                <th>SAP Ref</th>
                                <th className="text-right">Qty</th>
                                <th>Signature</th>
                                <th>Error</th>
                                <th></th>
                            </tr>
                        </thead>

                        <tbody>
                            {rows.length === 0 && (
                                <tr>
                                    <td colSpan={13} className="text-center py-8 text-muted-foreground">
                                        No deliveries.
                                    </td>
                                </tr>
                            )}

                            {rows.map((d) => {
                                const t = deliveryTotals(d);
                                const canPost = d.status === "delivered" && d.sap_status !== "posted_to_sap";
                                const hasError = !!d.sap_error;

                                return (
                                    <tr key={d.id} data-testid={`delivery-row-${d.id}`}>
                                        <td>
                                            <div className="font-mono">{d.doc_no}</div>
                                            <button
                                                onClick={() => setViewing(d)}
                                                className="text-[11px] text-muted-foreground hover:underline"
                                            >
                                                View details
                                            </button>
                                        </td>
                                        <td className="font-mono text-xs">{d.sales_order_no}</td>
                                        <td>{d.customer_name}</td>
                                        <td className="font-mono">{d.driver_code || "—"}</td>
                                        <td className="font-mono">{d.truck_code || "—"}</td>
                                        <td className="font-mono">{d.trip_code || "—"}</td>
                                        <td><StatusBadge status={d.status} /></td>
                                        <td><StatusBadge status={d.sap_status || "not_posted"} /></td>
                                        <td className="text-xs">
                                            <div>{sapLabel(d)}</div>
                                            {d.sap_docentry && (
                                                <div className="text-muted-foreground font-mono">
                                                    Entry {d.sap_docentry}
                                                </div>
                                            )}
                                        </td>
                                        <td className="sc-num">{num(t.qty)}</td>
                                        <td className="text-xs">
                                            <div>{signedBy(d)}</div>
                                            <div className="text-muted-foreground">{dt(signedAt(d))}</div>
                                        </td>
                                        <td className="max-w-[260px]">
                                            {hasError ? (
                                                <div className="text-xs text-red-600 truncate" title={d.sap_error}>
                                                    {d.sap_error}
                                                </div>
                                            ) : (
                                                <span className="text-muted-foreground">—</span>
                                            )}
                                        </td>
                                        <td className="text-right">
                                            <div className="flex flex-wrap justify-end gap-1">
                                                <button
                                                    onClick={() => setViewing(d)}
                                                    className="text-xs h-7 px-2 rounded border border-border hover:bg-secondary"
                                                >
                                                    Details
                                                </button>
                                                <button
                                                    onClick={() => setEditing(d)}
                                                    className="text-xs h-7 px-2 rounded border border-border hover:bg-secondary"
                                                    data-testid={`delivery-assign-${d.id}`}
                                                >
                                                    Assign
                                                </button>
                                                <button
                                                    onClick={() => sign(d)}
                                                    disabled={d.status === "delivered"}
                                                    className="text-xs h-7 px-2 rounded border border-border hover:bg-secondary disabled:opacity-40"
                                                    data-testid={`delivery-sign-${d.id}`}
                                                >
                                                    Sign
                                                </button>
                                                <button
                                                    onClick={() => postToSap(d)}
                                                    disabled={!canPost || busyId === d.id}
                                                    className="text-xs h-7 px-2 rounded border border-border hover:bg-secondary disabled:opacity-40"
                                                    title={d.sap_error || ""}
                                                >
                                                    {busyId === d.id ? "Posting…" : "Post SAP"}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {editing && (
                <div className="fixed inset-0 bg-black/40 grid place-items-center p-4 z-50" onClick={() => setEditing(null)}>
                    <div className="sc-card p-5 w-full max-w-md space-y-3 bg-background" onClick={(e) => e.stopPropagation()} data-testid="delivery-assign-modal">
                        <div className="font-semibold">Assign delivery {editing.doc_no}</div>

                        <select className="sc-input" value={editing.driver_code || ""} onChange={(e) => setEditing({ ...editing, driver_code: e.target.value })} data-testid="modal-driver">
                            <option value="">Select driver…</option>
                            {drivers.map((d) => <option key={d.driver_code} value={d.driver_code}>{d.driver_code} — {d.name}</option>)}
                        </select>

                        <select className="sc-input" value={editing.truck_code || ""} onChange={(e) => setEditing({ ...editing, truck_code: e.target.value })} data-testid="modal-truck">
                            <option value="">Select truck…</option>
                            {trucks.map((t) => <option key={t.truck_code} value={t.truck_code}>{t.truck_code} ({t.plate})</option>)}
                        </select>

                        <select className="sc-input" value={editing.trip_code || ""} onChange={(e) => setEditing({ ...editing, trip_code: e.target.value })} data-testid="modal-trip">
                            <option value="">Select trip…</option>
                            {trips.map((t) => <option key={t.trip_code} value={t.trip_code}>{t.trip_code} — {t.name}</option>)}
                        </select>

                        <div className="flex gap-2 justify-end">
                            <button onClick={() => setEditing(null)} className="h-9 px-3 rounded border border-border text-sm">Cancel</button>
                            <button onClick={assign} className="h-9 px-3 rounded bg-foreground text-background text-sm" data-testid="modal-assign-confirm">Save</button>
                        </div>
                    </div>
                </div>
            )}

            {viewing && (
                <div className="fixed inset-0 bg-black/40 grid place-items-center p-4 z-50" onClick={() => setViewing(null)}>
                    <div className="sc-card p-5 w-full max-w-5xl max-h-[90vh] overflow-y-auto space-y-4 bg-background" onClick={(e) => e.stopPropagation()} data-testid="delivery-details-modal">
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <div className="text-xs uppercase tracking-wider text-muted-foreground">Delivery Details</div>
                                <div className="text-lg font-semibold">{viewing.doc_no}</div>
                                <div className="text-sm text-muted-foreground">{viewing.customer_name}</div>
                            </div>

                            <button onClick={() => setViewing(null)} className="h-9 px-3 rounded border border-border text-sm">
                                Close
                            </button>
                        </div>

                        <div className="grid md:grid-cols-4 gap-3 text-sm">
                            <InfoBox label="SO" value={viewing.sales_order_no} mono />
                            <InfoBox label="Delivery Status" value={viewing.status} />
                            <InfoBox label="SAP Status" value={viewing.sap_status || "not_posted"} />
                            <InfoBox label="SAP Ref" value={viewing.sap_docnum || viewing.sap_docentry || "—"} mono />
                            <InfoBox label="Driver" value={viewing.driver_code || "—"} mono />
                            <InfoBox label="Truck" value={viewing.truck_code || "—"} mono />
                            <InfoBox label="Trip" value={viewing.trip_code || "—"} mono />
                            <InfoBox label="Signed By" value={signedBy(viewing)} />
                            <InfoBox label="Signed At" value={dt(signedAt(viewing))} />
                            <InfoBox label="Created" value={dt(viewing.created_at)} />
                            <InfoBox label="Updated" value={dt(viewing.updated_at)} />
                            <InfoBox label="Delivered Qty" value={num(deliveryTotals(viewing).qty)} />
                        </div>

                        {viewing.sap_error && (
                            <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700 whitespace-pre-wrap">
                                <div className="font-semibold mb-1">SAP Posting Error</div>
                                {viewing.sap_error}
                            </div>
                        )}

                        <div className="border border-border rounded-lg overflow-hidden">
                            <table className="w-full sc-table min-w-[850px]">
                                <thead>
                                    <tr>
                                        <th>Item</th>
                                        <th>Description</th>
                                        <th>Warehouse</th>
                                        <th>Batch/Tonality</th>
                                        <th className="text-right">Ordered</th>
                                        <th className="text-right">Previous Delivered</th>
                                        <th className="text-right">This Delivery</th>
                                        <th className="text-right">Remaining After</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {(viewing.lines || []).map((l) => (
                                        <tr key={l.line_id || l.id}>
                                            <td className="font-mono text-xs">{l.item_code}</td>
                                            <td>
                                                <div>{l.item_name}</div>
                                                <div className="text-[11px] text-muted-foreground">{l.size || l.description}</div>
                                            </td>
                                            <td className="font-mono">{l.warehouse_code}</td>
                                            <td className="text-xs">
                                                {(l.allocation?.allocated_batches || []).length > 0 ? (
                                                    (l.allocation?.allocated_batches || []).map((b) => (
                                                        <div key={b.batch_code || b.tonality}>
                                                            <span className="font-mono">{b.batch_code || b.tonality}</span>
                                                            <span className="text-muted-foreground"> — {num(b.quantity)}</span>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <span className="text-muted-foreground">—</span>
                                                )}
                                            </td>
                                            <td className="sc-num">{num(l.ordered_quantity ?? l.inquired_quantity ?? l.quantity)}</td>
                                            <td className="sc-num">{num(l.previous_delivered_quantity ?? 0)}</td>
                                            <td className="sc-num font-semibold">{num(l.deliver_quantity ?? l.quantity)}</td>
                                            <td className="sc-num">{num(l.remaining_after_delivery ?? l.remaining_quantity ?? 0)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div>
                            <div className="font-semibold text-sm mb-2">Status History</div>
                            <div className="space-y-2">
                                {(viewing.status_history || []).map((h, idx) => (
                                    <div key={`${h.status}-${idx}`} className="rounded border border-border p-2 text-xs">
                                        <div className="flex flex-wrap justify-between gap-2">
                                            <span className="font-semibold">{h.status}</span>
                                            <span className="text-muted-foreground">{dt(h.at)}</span>
                                        </div>
                                        <div className="text-muted-foreground">By: {h.by || "—"}</div>
                                        {h.comment && <div className="mt-1 whitespace-pre-wrap">{h.comment}</div>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export function Returns() {
    const { user } = useAuth();
    // The hidden-price group (price_visibility "none") sees no money on the Returns module either
    // (Sir Lynx 2026-10-29): no subtotal, service charge or replacement-PO amount.
    const showPrices = user?.price_visibility !== "none";
    const [rows, setRows] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [orders, setOrders] = useState([]);
    const [sel, setSel] = useState(null);
    const [qty, setQty] = useState({});
    const [rtype, setRtype] = useState("change_unpicked");
    const [reason, setReason] = useState("");
    const [riwRef, setRiwRef] = useState("");
    const [replacementPo, setReplacementPo] = useState("");
    const [replLookup, setReplLookup] = useState(null); // {found, replacement_po_no, doc_no, subtotal} | null | "loading"
    const [condAck, setCondAck] = useState(false);
    const [remaining, setRemaining] = useState({});
    const [hidden, setHidden] = useState({});
    const [photos, setPhotos] = useState([]);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [expanded, setExpanded] = useState(null);

    const load = async () => setRows((await api.get("/returns")).data);
    useEffect(() => { load(); }, []);

    const openForm = async () => {
        setErr(""); setShowForm(true); setSel(null); setQty({}); setReason(""); setRtype("change_unpicked");
        setRiwRef(""); setReplacementPo(""); setCondAck(false); setRemaining({}); setHidden({}); setPhotos([]);
        try {
            const [comp, adv] = await Promise.all([
                api.get("/non-organic/orders", { params: { status: "completed" } }),
                api.get("/non-organic/orders"),
            ]);
            const completed = (((comp.data && comp.data.orders) || comp.data || [])).filter((x) => x.sap_invoice_docnum || x.sap_invoice_docentry);
            const advance = (((adv.data && adv.data.orders) || adv.data || [])).filter((x) => ["advance_approved", "awaiting_stock", "awaiting_ar_reserve"].includes(x.status));
            const seen = new Set(); const all = [];
            for (const ord of [...completed, ...advance]) { if (!seen.has(ord.id)) { seen.add(ord.id); all.push(ord); } }
            setOrders(all);
        } catch (e) { setErr("Could not load orders."); }
    };

    const pickOrder = async (row) => {
        setSel(row || null); setHidden({}); setReplLookup(null);
        const q = {}; ((row && row.lines) || []).forEach((l) => { q[l.item_code] = 0; });
        setQty(q);
        if (row) {
            try { const { data } = await api.get(`/order-returnable/${row.id}`); setRemaining(data || {}); }
            catch { setRemaining({}); }
            // Only the PO already declared on the Sales Cart as THIS order's "Replacement to PO
            // No." may ever be used -- resolve it here so the field shows that one value instead
            // of accepting anything typed. The server enforces this independently either way.
            const srcPo = row.po_no || row.doc_no || "";
            if (srcPo) {
                setReplLookup("loading");
                try {
                    const { data } = await api.get("/returns/replacement-lookup", { params: { po_no: srcPo } });
                    setReplLookup(data);
                    setReplacementPo(data.found ? data.replacement_po_no : "");
                } catch { setReplLookup({ found: false }); setReplacementPo(""); }
            } else {
                setReplLookup({ found: false }); setReplacementPo("");
            }
        } else { setRemaining({}); }
    };

    const submit = async () => {
        if (!sel) { setErr("Pick an order to return."); return; }
        const isAdvanceCancel = !(sel.sap_invoice_docnum || sel.sap_invoice_docentry);
        const lines = ((sel.lines) || [])
            .filter((l) => !hidden[l.item_code])
            .map((l) => ({ item_code: l.item_code, warehouse_code: l.warehouse_code || "WH1", quantity: Number(qty[l.item_code] || 0), schema_name: l.schema_name }))
            .filter((l) => l.quantity > 0);
        if (!lines.length) { setErr("Enter a return quantity on at least one line."); return; }
        if (!reason.trim()) { setErr("Enter a reason for the return."); return; }
        if (needsAck && !condAck) { setErr("Tick the exchange-conditions checkbox before submitting."); return; }
        if (!photos.length && !isAdvanceCancel) { setErr("Attach at least one photo of the item before saving (for initial assessment)."); return; }
        setBusy(true); setErr("");
        try {
            const { data: created } = await api.post("/returns", {
                customer_code: sel.customer_code,
                sales_order_id: sel.id,
                invoice_no: String(sel.sap_invoice_docnum || sel.sap_invoice_docentry || ""),
                return_type: isAdvanceCancel ? "change_unpicked" : rtype, reason, lines,
                riw_ref: riwRef, replacement_po_no: replacementPo, conditions_ack: condAck,
            });
            const rid = created && created.id;
            if (rid) {
                for (const f of photos) {
                    const fd = new FormData();
                    fd.append("file", f);
                    fd.append("kind", "item_photo");
                    try { await api.post(`/returns/${rid}/attachments`, fd); } catch (_) { /* one bad photo must not lose the return */ }
                }
            }
            setShowForm(false); await load();
        } catch (e) {
            setErr(formatApiError(e) || "Could not create the return.");
        } finally { setBusy(false); }
    };

    const btn = "px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm disabled:opacity-60";
    // The exchange-conditions box is shown (and now REQUIRED) for every return that is not defective;
    // an order never invoiced in SAP is forced to an unpicked exchange, so it counts as one too.
    const needsAck = ((sel && !(sel.sap_invoice_docnum || sel.sap_invoice_docentry)) ? "change_unpicked" : rtype) !== "defective";

    return (
        <div className="p-6 space-y-4" data-testid="returns-page">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="sc-section-title">Sales</div>
                    <h1 className="text-2xl font-semibold tracking-tight">Sales Returns</h1>
                    <p className="text-sm text-muted-foreground mt-1">A return posts an A/R Credit Memo to SAP once the assigned approver signs off (Inquiry Dept for an exchange, Purchasing for a defective item).</p>
                </div>
                <button className={btn} onClick={openForm}>New Return</button>
            </div>

            {showForm && (
                <div className="sc-card p-4 space-y-3">
                    <div className="flex items-center justify-between">
                        <div className="sc-section-title">New Return</div>
                        <button className="text-sm text-muted-foreground" onClick={() => setShowForm(false)}>Close</button>
                    </div>
                    <div>
                        <label className="text-xs text-muted-foreground">Completed order (with SAP invoice)</label>
                        <select className="sc-input mt-1" value={(sel && sel.id) || ""} onChange={(e) => pickOrder(orders.find((x) => x.id === e.target.value))}>
                            <option value="">Select an order…</option>
                            {orders.map((x) => (
                                <option key={x.id} value={x.id}>{x.doc_no} · {x.customer_name} · Inv {x.sap_invoice_docnum || "—"} · PO {x.po_no || "—"}</option>
                            ))}
                        </select>
                    </div>
                    {sel && (
                        <div className="overflow-x-auto">
                            <div className="text-xs text-muted-foreground mb-1">Source PO: <span className="font-mono">{sel.po_no || sel.doc_no || "—"}</span></div>
                            <table className="w-full sc-table">
                                <thead><tr><th>Item</th><th className="text-right">Sold</th><th className="text-right">Returnable</th><th>Tonality</th><th className="text-right">Return qty</th><th></th></tr></thead>
                                <tbody>
                                    {((sel.lines) || []).filter((l) => !hidden[l.item_code]).map((l) => {
                                        const rem = remaining[l.item_code] ? Number(remaining[l.item_code].remaining) : Number(l.quantity);
                                        return (
                                        <tr key={l.item_code}>
                                            <td>{l.item_name || l.item_code}</td>
                                            <td className="text-right">{l.quantity}</td>
                                            <td className="text-right">{rem}</td>
                                            <td className="text-[11px] font-mono text-emerald-700">{(l.allocation?.allocated_batches?.length
                                                ? l.allocation.allocated_batches.map((b) => `${b.batch_code || b.tonality}×${Number(b.quantity || 0)}`).join(", ")
                                                : (l.batch_code || l.requested_tonality || "—"))}</td>
                                            <td className="text-right">
                                                <input type="number" min="0" max={rem} className="sc-input h-8 w-24 text-right"
                                                    value={qty[l.item_code] ?? 0}
                                                    onChange={(e) => setQty((q) => ({ ...q, [l.item_code]: e.target.value }))} />
                                            </td>
                                            <td><button className="text-rose-600 px-1" title="Remove from this return" onClick={() => setHidden((h) => ({ ...h, [l.item_code]: true }))}>×</button></td>
                                        </tr>
                                    );})}
                                </tbody>
                            </table>
                            {showPrices && (() => {
                                const sub = ((sel.lines) || []).filter((l) => !hidden[l.item_code]).reduce((a, l) => a + Number(qty[l.item_code] || 0) * Number(l.unit_price ?? l.dp_price ?? 0), 0);
                                let days = null;
                                if (sel.created_at) { const d = new Date(sel.created_at); if (!isNaN(d.getTime())) days = Math.floor((Date.now() - d.getTime()) / 86400000); }
                                const pct = (rtype !== "defective" && days !== null && days > 10 && days <= 30) ? 5 : 0;
                                const fmt = (n) => Number(n).toLocaleString(undefined, { minimumFractionDigits: 2 });
                                return (
                                    <div className="mt-1 ml-auto w-full max-w-xs text-xs space-y-0.5">
                                        <div className="flex justify-between"><span className="text-muted-foreground">Return subtotal</span><span className="font-mono">{fmt(sub)}</span></div>
                                        {pct > 0 && <div className="flex justify-between text-amber-700"><span>Service charge (5%{days !== null ? `, ${days} days` : ""})</span><span className="font-mono">{fmt(sub * pct / 100)}</span></div>}
                                        <div className="text-[10px] text-muted-foreground text-right pt-0.5">Return subtotal must be ≤ the replacement PO.</div>
                                    </div>
                                );
                            })()}
                        </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs text-muted-foreground">Return type</label>
                            <select className="sc-input mt-1" value={rtype} onChange={(e) => setRtype(e.target.value)}>
                                <option value="change_unpicked">Change of item (not yet picked up), Inquiry Dept</option>
                                <option value="change_picked_riw">Change of item (picked up, RIW), Inquiry Dept</option>
                                <option value="defective">Defective item, Purchasing</option>
                            </select>
                        </div>
                        <div>
                            <label className="text-xs text-muted-foreground">Reason</label>
                            <input className="sc-input mt-1" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for return" />
                        </div>
                    </div>
                    {rtype !== "defective" && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs text-muted-foreground">Replacement PO No. (required)</label>
                                <input className="sc-input mt-1 bg-secondary/40" readOnly
                                    value={replLookup === "loading" ? "Looking up…" : (replacementPo || "")}
                                    placeholder="Pick an order above" />
                                {replLookup && replLookup !== "loading" && (
                                    replLookup.found
                                        ? <div className="text-[11px] text-emerald-700 mt-1">From {replLookup.doc_no} — the only PO that may replace this order.</div>
                                        : <div className="text-[11px] text-rose-600 mt-1">No Sales Cart order declares this order's PO as its "Replacement to PO No." yet. Create that order first, then return.</div>
                                )}
                            </div>
                            {rtype === "change_picked_riw" && (
                                <div>
                                    <label className="text-xs text-muted-foreground">RIW form reference (required)</label>
                                    <input className="sc-input mt-1" value={riwRef} onChange={(e) => setRiwRef(e.target.value)} placeholder="RIW form no." />
                                </div>
                            )}
                        </div>
                    )}
                    {needsAck && (
                        <label className="flex items-start gap-2 text-xs text-muted-foreground">
                            <input type="checkbox" className="mt-0.5" checked={condAck} onChange={(e) => setCondAck(e.target.checked)} />
                            <span><b className="text-rose-600">(Required)</b> I confirm the exchange conditions: within 30 days of purchase (a 5% service charge applies after 10 days), item in good condition with its original box, and the replacement equals or exceeds the returned item's value. Not valid for PRF/project POs, indent/irregular items, or slabs unless defective.</span>
                        </label>
                    )}
                    <div>
                        <label className="text-xs text-muted-foreground">Item photos (required, for initial assessment)</label>
                        <input type="file" multiple accept="image/*" className="sc-input mt-1 text-xs"
                            onChange={(e) => setPhotos(Array.from(e.target.files || []))} data-testid="return-photos" />
                        <div className="text-[11px] text-muted-foreground mt-1">{photos.length ? `${photos.length} photo(s) selected` : "Attach at least one photo of the item to be returned before saving."}</div>
                    </div>
                    {err && <div className="text-sm text-rose-600">{err}</div>}
                    <div className="flex justify-end">
                        <button className={btn} disabled={busy || (needsAck && !condAck)} onClick={submit} title={needsAck && !condAck ? "Tick the exchange-conditions checkbox first" : ""}>{busy ? "Submitting…" : "Create return"}</button>
                    </div>
                </div>
            )}

            <div className="sc-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full sc-table min-w-[960px]">
                        <thead>
                            <tr>
                                <th>Doc no</th>
                                <th>Customer</th>
                                <th>PO No.</th>
                                <th>Type</th>
                                <th>Reason</th>
                                <th>Status</th>
                                <th>SAP Credit Memo</th>
                                {showPrices && <th className="text-right">Subtotal</th>}
                            </tr>
                        </thead>

                        <tbody>
                            {rows.length === 0 && (
                                <tr>
                                    <td colSpan={8} className="text-center py-8 text-muted-foreground">
                                        No returns yet. Use “New Return”.
                                    </td>
                                </tr>
                            )}

                            {rows.map((r) => (
                                <Fragment key={r.id}>
                                    <tr className="cursor-pointer hover:bg-secondary/40" onClick={() => setExpanded(expanded === r.id ? null : r.id)} title="Show attachments (RIW form)">
                                        <td className="font-mono">{r.doc_no}</td>
                                        <td>{r.customer_name}</td>
                                        <td className="font-mono text-xs">{r.source_po_no || r.replacement_po_no || "—"}</td>
                                        <td>{({change_unpicked: "Change (unpicked)", change_picked_riw: "Change (RIW)", defective: "Defective"}[r.return_type]) || r.return_type}{r.service_charge ? ` · ${Number(r.service_charge_pct)}% chg` : ""}</td>
                                        <td className="text-muted-foreground">{r.reason}</td>
                                        <td><StatusBadge status={r.status} /></td>
                                        <td className="font-mono text-xs">{r.sap_credit_memo_docnum ? `#${r.sap_credit_memo_docnum}` : (r.sap_status && r.sap_status !== "not_posted" ? r.sap_status : "—")}</td>
                                        {showPrices && <td className="sc-num">{Number(r.subtotal).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>}
                                    </tr>
                                    {expanded === r.id && (
                                        <tr>
                                            <td colSpan={8} className="bg-secondary/20 p-3">
                                                <div className="text-xs mb-2 flex flex-wrap gap-x-6 gap-y-1">
                                                    {showPrices && <span>Return subtotal: <b className="font-mono">{Number(r.subtotal || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</b></span>}
                                                    {showPrices && r.service_charge ? <span className="text-amber-700">Service charge ({Number(r.service_charge_pct)}%): <b className="font-mono">{Number(r.service_charge).toLocaleString(undefined, { minimumFractionDigits: 2 })}</b></span> : null}
                                                    {r.replacement_po_no ? <span>Replacement PO: <b className="font-mono">{r.replacement_po_no}</b></span> : null}
                                                </div>
                                                {(r.lines || []).some((l) => l.actual_quantity != null || l.actual_tonality) && (
                                                    <div className="text-[11px] mb-2">
                                                        <div className="uppercase tracking-wide text-muted-foreground mb-0.5">QC receiving (actual)</div>
                                                        {(r.lines || []).map((l, i) => (
                                                            <div key={i} className="font-mono">{l.item_name || l.item_code}: {l.actual_tonality || (l.allocation?.allocated_batches || []).map((b) => b.tonality || b.batch_code).filter(Boolean).join(", ") || "—"} × {l.actual_quantity != null ? l.actual_quantity : l.quantity}{l.actual_quantity != null && Number(l.actual_quantity) !== Number(l.quantity) ? (" (declared " + l.quantity + ")") : ""}</div>
                                                        ))}
                                                    </div>
                                                )}
                                                <Attachments collection="returns" docId={r.id} editable={r.user_id === user?.id || user?.role === "admin"} kinds={[["item_photo", "For assessment"]]} defaultKind="item_photo" displayKinds={[["item_photo", "For assessment"], ["received_riw", "Received RIW (QC)"], ["riw_form", "RIW form"], ["other", "Other"]]} />
                                            </td>
                                        </tr>
                                    )}
                                </Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function SummaryCard({ label, value }) {
    return (
        <div className="sc-card p-4">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className="text-lg font-semibold mt-1">{value}</div>
        </div>
    );
}

function InfoBox({ label, value, mono = false }) {
    return (
        <div className="rounded border border-border p-3">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className={`mt-1 ${mono ? "font-mono text-xs" : ""}`}>{value}</div>
        </div>
    );
}
